import { Entry } from "contentful";
import { NextRouter } from "next/router";
import { difference, equals } from "ramda";

import { TextStyle } from "~/enums";
import Editorials from "~/types/editorials";

import localeConfig from "../../locale.config";
import { isActiveEntry } from "./editorial-utils";

type TextStylesConfiguration = {
  styles: TextStyle[];
  tag: String[];
};

type StyledTextToken = {
  token: string;
  style: TextStyle;
};

const DEFAULT_ALT_STYLE_CONFIGS: TextStylesConfiguration = {
  styles: [TextStyle.DEFAULT, TextStyle.HIGHLIGHTED],
  tag: ["%", "%"], // more performant than using charAt on a string -> use chars not strings
};

/**
 * Creates an array of text tokens with a bound style reference
 * @param inText - the text to tokenize
 * @param configs - the configurations
 * @returns - an array of text tokens with a bound style reference
 */
function getStyledTextTokens(
  inText: string,
  configs: TextStylesConfiguration = DEFAULT_ALT_STYLE_CONFIGS
): StyledTextToken[] {
  let tagHead: number = 0;
  let styleHead: number = 0;
  let isEscaping: boolean = false;
  let tokens: StyledTextToken[] = [];
  let token: string = "";
  let ambiguousChunk: string = "";
  for (let head: number = 0; head < inText.length; head++) {
    const charAt: string = inText[head];
    if (charAt === configs.tag[tagHead]) {
      if (isEscaping) {
        isEscaping = false;
        token += charAt;
      } else if (++tagHead >= configs.tag.length) {
        tagHead = 0;
        if (token.length > 0) {
          tokens.push({
            token: token,
            style: configs.styles[styleHead % configs.styles.length],
          });
        }
        token = "";
        ambiguousChunk = "";
        styleHead++;
      } else {
        ambiguousChunk += charAt;
        if (head === inText.length - 1) {
          token += ambiguousChunk;
        }
      }
    } else {
      tagHead = 0;
      token += ambiguousChunk + charAt;
      ambiguousChunk = "";
    }
  }

  if (token.length > 0) {
    tokens.push({
      token: token,
      style: configs.styles[styleHead++ % configs.styles.length],
    });
  }
  return tokens;
}

function escapeHtmlParagraph(htmlString: string): string {
  return htmlString.replace(/<p>/g, "").replace(/<\/p>/g, "");
}

function sanitizeContentfulUrl(link: Entry<Editorials.Link>, router: NextRouter) {
  if (!isActiveEntry(link)) {
    return "#";
  }

  const pageRef = link.fields.pageRef?.published ? link.fields.pageRef : undefined;

  if (pageRef) {
    const contentfulLocale =
      localeConfig.contentfulLocales[(router.locale as keyof typeof localeConfig.contentfulLocales) ?? "en-ae"];

    if (pageRef.contentType === "categoryBinding") {
      return "/c/" + pageRef.slugs[contentfulLocale] + "/";
    }
    if (pageRef.contentType === "editorialPage") {
      return "/" + pageRef.slugs[contentfulLocale] + "/";
    }
  }

  return link.fields.url ?? "#";
}

function findClosestMatch(input: string, candidates: string[]) {
  function normalize(value: string) {
    return value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replaceAll(/[^a-z\s]/g, "")
      .split(/\s+/);
  }

  function eitherIsSubset(lhs: string[], rhs: string[]) {
    return difference(lhs, rhs).length === 0 || difference(rhs, lhs).length === 0;
  }

  if (!input || candidates.length === 0) {
    return null;
  }

  // This adds another iteration over the candidates, but lets us skip
  // all the replacements and `RegExp` operations.
  if (candidates.includes(input)) {
    return input;
  }

  const inputParts = normalize(input);

  let match = candidates.find((c) => equals(normalize(c), inputParts));

  if (!match) {
    // Accept a partial match
    match = candidates.find((c) => eitherIsSubset(inputParts, normalize(c)));
  }

  return match ?? null;
}

function findClosestSelectMatch(input: string, options: { value: string; label: string }[]) {
  const match = findClosestMatch(
    input,
    options.map((o) => o.label)
  );

  if (match) {
    return options.find((o) => o.label === match)?.value;
  }

  return undefined;
}

const textUtils = {
  getStyledTextTokens: getStyledTextTokens,
  escapeHtmlParagraph,
  sanitizeContentfulUrl,
  findClosestMatch,
  findClosestSelectMatch,
};

export default textUtils;
